import { withPrefix } from "gatsby";
import { ComponentsDefinitionNode, ComponentsDefinitionsQuery } from "queries";
import { PropertyProps } from "./shared/components/Property/Property";
import type { Member, Property } from "./types";

export function isFunction(obj: Member): boolean {
    return obj.kind === "function";
}

export function isMember(obj: Member): boolean {
    return obj.kind === "member";
}

export function isTypeDef(obj: Member): boolean {
    return obj.kind === "typedef";
}

export function isInterface(obj: Member): boolean {
    return obj.kind === "interface";
}

export function getMethodSignature(name: string, params: Property[], returns: Property[]) {
    const returnType: string = returns?.[0].type?.names?.[0] || "unknown";
    const paramString = params?.map((param) => `${param.name}${param.optional ? "?" : ""}`).join(", ");
    return `${name}(${paramString}) => ${returnType}`;
}

export function resolveComplexTypes(typeDefs: Record<string, Member>, properties: Property[]): Property[] {
    return properties?.map((property) => {
        const propertyType = property.type?.names[0];
        const propertyTypeParameter = propertyType?.match(/(?<=<)(.*)(?=>)/g)?.pop();
        const typeDef: Member | undefined = !!propertyType?.length
            ? typeDefs[propertyTypeParameter || propertyType]
            : undefined;

        if (!typeDef || (!typeDef.properties && !typeDef.params)) return property;
        if (typeDef.params) {
            property.type = {
                names: [getMethodSignature("", typeDef.params, typeDef.returns)]
            };
        }

        return {
            ...property,
            nested: resolveComplexTypes(typeDefs, typeDef.properties || [...typeDef.params, ...typeDef.returns])
        };
    });
}

/*
This is used to get properties of extended interfaces. For the below example,
this returns all JSDoc annotated properties of TaskListItemProps and TaskContextProps.
If e.g. TaskContextProps has additional inhereted properties, they will recursively be included as well.

@typedef {TaskListItemProps | TaskContextProps} TaskListItem.TaskListItemChildrenProps
*/
export const getInheretedProperties = (
    definitionNode: ComponentsDefinitionNode | undefined,
    allDefinitionsQuery: ComponentsDefinitionsQuery
): PropertyProps[] => {
    const inheretedProperties: PropertyProps[] = [];
    definitionNode?.type?.names?.forEach((name) => {
        if (name.split(".").length > 2) {
            throw new Error(
                `Nesting more than one namespace/type-prefix is unconventional. (i.e. a.b.MyType) ${name} has more than one dot, please stick to one.`
            );
        }
        // MyDynamicComponent.MyProps -> MyProps
        name = name.split(".")?.[1] || name;

        const extendedDefinitionNode = allDefinitionsQuery.edges.find(({ node }) => node.name === name)?.node;
        if (extendedDefinitionNode?.properties) {
            inheretedProperties.push(...extendedDefinitionNode.properties);
        }
        // If the extended interface itself extends another interface, we need to recurse
        // and include all inherited properties from there as well.
        // If "extendedDefinitionNode.type.names" exists, we know it has
        // the @typedef {SOMETHING | SOMETHING ELSE} MyType annotation, from which we can get more properties.
        // In this example, extendedDefinitionNode.type.names = ["SOMETHING", "SOMETHING ELSE"]
        if (extendedDefinitionNode?.type?.names) {
            inheretedProperties.push(...getInheretedProperties(extendedDefinitionNode, allDefinitionsQuery));
        }
    });

    return inheretedProperties;
};

export const resolveNestedProperties = (processed: PropertyProps[], current: PropertyProps): PropertyProps[] => {
    const nameParts = current.name.split(".");
    const parent = processed.find((property) => property.name === nameParts[0]);
    const shortenedName = nameParts.slice(1).join(".");

    if (!parent) {
        processed.push(current);
    } else if (shortenedName) {
        current.name = shortenedName;
        parent.nested = resolveNestedProperties(parent.nested || [], current);
    }
    return processed;
};
// This is used to get the second last element from the path as an extra / has been added to each of the page path to fix the FLXUIAPPIN-108 favicon issue
export const getLastValidElementFromPath = (path: string) => path?.split("/").slice(-2, -1)[0];

// This function is used update the internal links present in the property description and class description with the correct Base path and Prefix path.
export const addPrefixAndUpdateInternalLinks = (description: string, paths: Array<{ path: string }>): string => {
    // Obtain the hrefs with links other than http and https
    const hrefAttrs = description?.match(/href=('|")(?!https?:\/\/).+?("|')/g);

    if (hrefAttrs) {
        hrefAttrs.forEach((hrefAttr) => {
            // Extract the value of the href attribute and strip the quotes at the end
            const s = hrefAttr.split("=")[1].slice(1, -1);
            const internalLink = s.split("#");
            // Get the base path
            let link: string = paths.filter(
                ({ path }: { path: string }) => getLastValidElementFromPath(path) === internalLink[0].replace(".", "․")
            )[0]?.path;
            // If link is undefined
            link = link || "/";
            // If there is a link within the page add that element after #
            link = internalLink.length > 1 ? `${link.slice(0, -1)}#${internalLink[1]}` : link;
            link = withPrefix(link);
            // Add the quotes that were removed earlier
            link = `href="${link}"`;
            description = description?.replace(hrefAttr, link);
        });
    }
    return description;
};
